import { LoaderFunction, useLoaderData, useNavigate } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import {
  ALGOLIA_PRODUCT_TYPES,
  buildAnalyticsTags,
  buildCatalogFilters,
  type CatalogFilterInput,
  getBestSellingIndexName,
} from "@/utils/algoliaCatalog";
import { getAlgoliaSearchClient } from "@/utils/algoliaSearchClient";
import {
  getPlatform,
  getPlatformTheme,
  getRetailproductsAreRetailProductsVisible,
  getShop,
  getSkindropIsVisibleOnMarketplace,
  getUser,
} from "api/methods";
import { STALE_TIME } from "api/staleTimes";
import { Icon } from "atoms/icon/Icon";
import { fetchCatalogLocationId } from "hooks/useCatalogLocationId";
import ChevronRight from "atoms/icon/icons/ic_chevron_right_small_ol.svg";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { queryClient } from "pages/Root";
import { notNull } from "toolbox/Guards";
import { waitObj } from "toolbox/Promise";

import { ShopListHeader } from "./ShopListHeader/ShopListHeader";

/** Algolia's ceiling for `hitsPerPage`: every treatment and package is read, so a concern only on low-ranked records still has examples. */
const MAX_EXAMPLE_HITS = 1000;
const EXAMPLES_PER_CONCERN = 3;

type ConcernOverview = {
  /** Visible treatments and packages per concern id, from one facet count. */
  countByConcern: Record<string, number>;
  /** A few best-selling titles per concern id, from the same request's hits. */
  examplesByConcern: Record<string, string[]>;
};

type ConcernOverviewInput = Pick<
  CatalogFilterInput,
  "platformId" | "platformLocationId" | "visibility"
>;

/**
 * One query answers every concern at once: the `concernIds` facet says how many treatments and
 * packages each concern has, and the top-selling hits of the same response supply the examples.
 */
export const fetchConcernOverview = async (
  input: ConcernOverviewInput,
): Promise<ConcernOverview> => {
  const { results } = await getAlgoliaSearchClient().search<{
    title: string;
    concernIds: string[];
  }>({
    requests: [
      {
        indexName: getBestSellingIndexName(),
        query: "",
        hitsPerPage: MAX_EXAMPLE_HITS,
        filters: buildCatalogFilters({
          ...input,
          productTypes: [ALGOLIA_PRODUCT_TYPES.treatment, ALGOLIA_PRODUCT_TYPES.package],
        }),
        facets: ["concernIds"],
        attributesToRetrieve: ["title", "concernIds"],
        analytics: false,
        analyticsTags: buildAnalyticsTags(input.platformId, "concern"),
      },
    ],
  });
  const [response] = results;
  if (!response || !("hits" in response)) {
    throw new Error("Algolia concern overview returned an unexpected response shape.");
  }

  const examplesByConcern: Record<string, string[]> = {};
  for (const hit of response.hits) {
    for (const concernId of hit.concernIds ?? []) {
      const examples = (examplesByConcern[concernId] ??= []);
      if (examples.length < EXAMPLES_PER_CONCERN) examples.push(hit.title);
    }
  }

  return { countByConcern: response.facets?.concernIds ?? {}, examplesByConcern };
};

const concernOverviewQuery = (input: ConcernOverviewInput) => ({
  queryKey: [
    "algolia/concern-overview",
    input.platformId,
    input.platformLocationId ?? null,
    !!input.visibility.areRetailProductsVisible,
    !!input.visibility.isSkinDropVisible,
  ],
  queryFn: () => fetchConcernOverview(input),
  staleTime: 1000 * 60,
});

const load = async () => {
  const shopData = queryClient.fetchQuery({ ...getShop.keyed() });
  const theme = queryClient.fetchQuery({ ...getPlatformTheme.keyed() });
  const platform = queryClient.fetchQuery({
    ...getPlatform.keyed(),
    staleTime: STALE_TIME.platformConfig,
  });
  const user = queryClient.fetchQuery({ ...getUser.keyed(), staleTime: 1000 * 60 * 2 });
  const areRetailProductsVisible = queryClient.fetchQuery({
    ...getRetailproductsAreRetailProductsVisible.keyed(),
    staleTime: 1000 * 10,
  });
  // Skindrop visibility is non-critical; if the endpoint fails, default to false
  const isSkinDropVisible = queryClient
    .fetchQuery({
      ...getSkindropIsVisibleOnMarketplace.keyed(),
      staleTime: 1000 * 10,
      retry: false,
    })
    .catch(() => false);

  const overview = Promise.all([platform, user, areRetailProductsVisible, isSkinDropVisible]).then(
    async ([platformData, userData, retailVisible, skinDropVisible]) => {
      const input = {
        platformId: platformData.id,
        platformLocationId: await fetchCatalogLocationId(queryClient, userData),
        visibility: {
          areRetailProductsVisible: retailVisible,
          isSkinDropVisible: skinDropVisible,
        },
      };
      return queryClient.fetchQuery(concernOverviewQuery(input));
    },
  );

  return waitObj({ shopData, theme, overview });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const shopBrowseByConcernsLoader: LoaderFunction = () => {
  return load();
};

const ShopBrowseByConcernPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { shopData, theme, overview } = useLoaderData() as LoaderData;
  const headerImage = theme.resources?.find(
    (resource) => resource.key === "browse-concern-header",
  )?.url;

  const concerns = shopData.concerns?.map((concern) => {
    const count = overview.countByConcern[concern.id] ?? 0;
    if (count === 0) return null;

    return {
      id: concern.id,
      name: concern.name,
      count,
      // A concern means little on its own; naming what it actually maps to is
      // what makes the choice answerable.
      description: concern.description,
      // A few examples, not the whole list — the count already says how many
      // there are, and a long list stops being scannable.
      examples: overview.examplesByConcern[concern.id] ?? [],
    };
  });

  return (
    <div className="flex flex-col bg-two">
      {/* Shop List Header */}
      <ShopListHeader
        bgGradient
        title={t("shopBrowseByConcernPage.title")}
        subtitle={t("shopBrowseByConcernPage.subtitle")}
        backgroundImageUrl={headerImage}
      />

      {/* Desktop: concern cards that show what each concern actually treats. */}
      <div className="mx-auto hidden w-full max-w-screen-xl gap-6 px-6 py-10 desk:grid desk:grid-cols-2 desklg:grid-cols-3 desklg:px-8 desk2xl:max-w-screen-2xl">
        {concerns?.filter(notNull).map((concern) => (
          <button
            key={concern.id}
            type="button"
            className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-outline-grey bg-white text-left shadow-subtle transition-shadow hover:shadow-lg"
            onClick={() => navigate(`/shop/concern/${concern.id}`)}
          >
            {/* No artwork: concerns carry no image of their own, and borrowing one
                from a treatment repeats the same photo across every card, since
                treatments span multiple concerns. The concern's own description
                and a few example treatments say far more. */}
            <div className="flex flex-1 flex-col gap-3 p-6">
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-serif text-h3 text-primary">{concern.name}</span>
                <span className="whitespace-nowrap text-body2 text-secondary">
                  {t("shopBrowseByConcernPage.treatmentCount", { count: concern.count })}
                </span>
              </div>

              {concern.description && (
                <p className="line-clamp-2 text-body2 text-secondary">{concern.description}</p>
              )}

              <ul className="mt-auto flex flex-wrap gap-x-2 gap-y-1 pt-1">
                {concern.examples.map((example) => (
                  <li
                    key={example}
                    className="rounded-full bg-background-two px-3 py-1 text-body3 text-primary"
                  >
                    {example}
                  </li>
                ))}
              </ul>
            </div>
          </button>
        ))}
      </div>

      {/* Phone: the same choices as a compact list. */}
      <div className="m-4 flex flex-col divide-y divide-solid divide-background-two overflow-clip rounded-lg bg-white shadow-subtle desk:hidden">
        {concerns &&
          concerns.filter(notNull).map((concern) => (
            <div
              key={concern?.id}
              className="flex h-14 w-full cursor-pointer flex-row items-center"
              onClick={() => navigate(`/shop/concern/${concern.id}`)}
            >
              <div className="flex w-full px-4 py-4 text-bold2 text-primary">{concern?.name}</div>
              <div className="flex w-full justify-end py-4 pl-4 text-body2 text-secondary">
                {t("shopBrowseByConcernPage.treatmentCount", { count: concern?.count })}
              </div>
              <div className="px-2">
                <Icon svg={ChevronRight} size="xsmall" color="brandColor" />
              </div>
            </div>
          ))}
      </div>

      <RepeatMDFooter />
    </div>
  );
};

export default ShopBrowseByConcernPage;
